import { findSeasonRoom, isOffered, isSet } from './inheritance';

// Season categories (API enum ContractSeasonCategoryEnum). Colours follow PRD
// §7; the DMC app has no --status-* tokens, so they map to the Tailwind
// palette (Super Peak red, Peak purple, High orange, Mid green, Low blue,
// Off gray-500, Closed gray-900). Full class names so Tailwind keeps them.
export const CATEGORIES = [
  {
    value: 'super_peak',
    label: 'Super Peak Season',
    dot: 'bg-red-500',
    text: 'text-red-600',
  },
  {
    value: 'peak',
    label: 'Peak Season',
    dot: 'bg-purple-500',
    text: 'text-purple-600',
  },
  {
    value: 'high',
    label: 'High Season',
    dot: 'bg-orange-500',
    text: 'text-orange-600',
  },
  {
    value: 'mid',
    label: 'Mid Season',
    dot: 'bg-green-500',
    text: 'text-green-600',
  },
  {
    value: 'low',
    label: 'Low Season',
    dot: 'bg-blue-500',
    text: 'text-blue-600',
  },
  {
    value: 'off',
    label: 'Off Season',
    dot: 'bg-gray-500',
    text: 'text-gray-500',
  },
  {
    value: 'closed',
    label: 'Closed',
    dot: 'bg-gray-900',
    text: 'text-gray-900',
  },
];

export const CLOSED = 'closed';

export const categoryOf = (season) =>
  CATEGORIES.find(({ value }) => value === season.category) ?? CATEGORIES[3];

/** "1 rate", "3 rates". */
export const plural = (count, word) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

// ISO weekdays: 1 = Monday … 7 = Sunday.
export const WEEKDAYS = [
  { value: 1, short: 'Mo', label: 'Mon' },
  { value: 2, short: 'Tu', label: 'Tue' },
  { value: 3, short: 'We', label: 'Wed' },
  { value: 4, short: 'Th', label: 'Thu' },
  { value: 5, short: 'Fr', label: 'Fri' },
  { value: 6, short: 'Sa', label: 'Sat' },
  { value: 7, short: 'Su', label: 'Sun' },
];

export const DAY_PRESETS = [
  { label: 'All days', days: [1, 2, 3, 4, 5, 6, 7] },
  { label: 'Sun–Thu', days: [1, 2, 3, 4, 7] },
  { label: 'Fri–Sat', days: [5, 6] },
  { label: 'Mon–Fri', days: [1, 2, 3, 4, 5] },
  { label: 'Sat–Sun', days: [6, 7] },
];

export const sortDays = (days) => [...days].sort((a, b) => a - b);

export const presetOf = (days) => {
  const key = sortDays(days).join();
  return DAY_PRESETS.find((preset) => preset.days.join() === key);
};

/** Preset name, else "Mon, Wed", else "No day". */
export const daysLabel = (days) =>
  presetOf(days)?.label ??
  (WEEKDAYS.filter(({ value }) => days.includes(value))
    .map(({ label }) => label)
    .join(', ') ||
    'No day');

/** yyyy-mm-dd → dd/mm/yy. */
export const shortDate = (isoDate) => {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year.slice(2)}`;
};

/** Offered rooms without an F price (or G with `field` 'groupPrice') in a
 * season (0 for Closed). */
export const missingRatesCount = (model, season, field = 'fitPrice') =>
  season.category === CLOSED
    ? 0
    : model.rooms.filter((room) => {
        const at = { roomId: room.id, seasonId: season.id };
        return (
          isOffered(model, at) &&
          !isSet(findSeasonRoom(model, season.id, room.id)?.[field])
        );
      }).length;

/** "Add season" default: category Mid, all days, one empty period. Unknown
 * ids are created by the PUT. */
export const createSeason = (position) => ({
  id: `new-season-${Date.now()}`,
  name: '',
  category: 'mid',
  daysOfWeek: DAY_PRESETS[0].days,
  position,
  periods: [{ startAt: null, endAt: null }],
});

/** Coverage "Mark as closed": a Closed season over a gap's dates and days. */
export const createClosedSeason = (gap, position) => ({
  ...createSeason(position),
  name: 'Hotel closed',
  category: CLOSED,
  daysOfWeek: gap.daysOfWeek,
  periods: [{ startAt: gap.startAt, endAt: gap.endAt }],
});

// Season-scoped data (rates, extra-bed prices, offered flags, room × season
// overrides) all live in `seasonRooms`, keyed by seasonId + roomId.
const withoutSeasonRooms = (model, seasonId) =>
  model.seasonRooms.filter((seasonRoom) => seasonRoom.seasonId !== seasonId);

const renumber = (seasons) =>
  seasons.map((season, position) => ({ ...season, position }));

const isSaved = (id) => !String(id).startsWith('new-');

/** "<name> (copy)" right after the source, with its season rooms; periods
 * blanked. The copy names its saved source so the save carries over the
 * source's TO distribution values (KD-3896). Returns the next model and the
 * copy's id. */
export const duplicateSeason = (model, seasonId) => {
  const index = model.seasons.findIndex(({ id }) => id === seasonId);
  const source = model.seasons[index];
  const copy = {
    ...source,
    id: `new-season-${Date.now()}`,
    name: `${source.name || 'Untitled'} (copy)`,
    daysOfWeek: [...source.daysOfWeek],
    periods: source.periods.map(() => ({ startAt: null, endAt: null })),
    duplicatedFromSeasonId: isSaved(source.id)
      ? source.id
      : source.duplicatedFromSeasonId,
  };
  const copiedRooms = model.seasonRooms
    .filter((seasonRoom) => seasonRoom.seasonId === seasonId)
    // Shallow is enough: the model is only ever updated immutably.
    .map((seasonRoom) => ({ ...seasonRoom, seasonId: copy.id }));

  return {
    id: copy.id,
    model: {
      ...model,
      seasons: renumber([
        ...model.seasons.slice(0, index + 1),
        copy,
        ...model.seasons.slice(index + 1),
      ]),
      seasonRooms: [...model.seasonRooms, ...copiedRooms],
    },
  };
};

export const deleteSeason = (model, seasonId) => ({
  ...model,
  seasons: renumber(model.seasons.filter(({ id }) => id !== seasonId)),
  seasonRooms: withoutSeasonRooms(model, seasonId),
});

/** Whether moving `season` to `category` crosses the Closed boundary (needs
 * confirmation). */
export const crossesClosed = (season, category) =>
  (season.category === CLOSED) !== (category === CLOSED);

/** Sets the category; crossing the Closed boundary drops the season's rooms
 * so no hidden prices stay behind and a reopened season starts empty. */
export const setSeasonCategory = (model, seasonId, category) => {
  const season = model.seasons.find(({ id }) => id === seasonId);
  return {
    ...model,
    seasons: model.seasons.map((current) =>
      current.id === seasonId ? { ...current, category } : current,
    ),
    seasonRooms: crossesClosed(season, category)
      ? withoutSeasonRooms(model, seasonId)
      : model.seasonRooms,
  };
};

export const CLOSED_CONFIRMATION = {
  toClosed:
    'Mark this season as Closed? Its rates and room conditions will no longer apply (hotel closed for all rooms).',
  reopen: 'Reopen this season? Rates will have to be entered for every room.',
};

/** Merges `patch` into the room × season, creating it (offered, nothing
 * overridden) when missing. */
export const setSeasonRoom = (model, { seasonId, roomId }, patch) => {
  const exists = Boolean(findSeasonRoom(model, seasonId, roomId));
  return {
    ...model,
    seasonRooms: exists
      ? model.seasonRooms.map((seasonRoom) =>
          seasonRoom.seasonId === seasonId && seasonRoom.roomId === roomId
            ? { ...seasonRoom, ...patch }
            : seasonRoom,
        )
      : [
          ...model.seasonRooms,
          {
            seasonId,
            roomId,
            offered: true,
            translations: [],
            extraBedPrices: [],
            ...patch,
          },
        ],
  };
};

/** { comparator: 'below', age: 6 } → "< 6 yo". */
export const ageGroupLabel = ({ comparator, age }) =>
  `${comparator === 'above' ? '>' : '<'} ${age ?? '?'} yo`;

/** Sets the F or G extra-bed price of one room × season × age group; null
 * clears it (the age-group default applies again). F and G override
 * independently: a row with neither goes. */
export const setExtraBedPrice = (model, at, kind, value) => {
  const { seasonId, roomId, ageGroupId } = at;
  const prices = findSeasonRoom(model, seasonId, roomId)?.extraBedPrices ?? [];
  const current = prices.find((price) => price.ageGroupId === ageGroupId);
  const next = { ageGroupId, fitPrice: null, groupPrice: null, ...current };
  next[kind] = value;
  const others = prices.filter((price) => price.ageGroupId !== ageGroupId);
  return setSeasonRoom(
    model,
    { seasonId, roomId },
    {
      extraBedPrices:
        isSet(next.fitPrice) || isSet(next.groupPrice)
          ? [...others, next]
          : others,
    },
  );
};

/** 12500.5 → "12 500.5" (space thousands separator); null → "". */
export const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const [integer, decimals] = String(value).split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decimals === undefined ? grouped : `${grouped}.${decimals}`;
};

/** "12 500,5" → 12500.5; "" → null; anything else non-numeric → undefined
 * (caller ignores it). */
export const parsePrice = (text) => {
  const compact = text.replace(/\s/g, '').replace(',', '.');
  if (compact === '') return null;
  const value = Number(compact);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

// Room conditions, set per room (all seasons) and overridable per room ×
// season. `null` / empty translations = inherit.
export const CONDITION_FIELDS = [
  'mealPlanId',
  'minimumStay',
  'allotmentCount',
  'sharedAllotmentCount',
  'releaseDays',
  'freeSale',
  'translations',
];

// ponytail: the screen edits the English extra information only; other
// locales already stored are kept as they are.
export const EXTRA_INFORMATION_LOCALE = 'en';

export const extraInformationOf = (translations) =>
  (translations ?? []).find(({ locale }) => locale === EXTRA_INFORMATION_LOCALE)
    ?.extraInformation ?? '';

/** Sets the English extra information; an empty text removes the entry. */
export const withExtraInformation = (translations, text) => [
  ...(translations ?? []).filter(
    ({ locale }) => locale !== EXTRA_INFORMATION_LOCALE,
  ),
  ...(text
    ? [{ locale: EXTRA_INFORMATION_LOCALE, extraInformation: text }]
    : []),
];

/**
 * Adds the hotel's rooms the contract has no row for yet (new contract, room
 * added to the hotel later), as new rows inheriting every condition. The PUT
 * creates rows for unknown ids. External-supplier-only rooms are not sold
 * through contracts.
 */
export const withHotelRooms = (model, hotelRooms) => {
  const known = new Set(model.rooms.map(({ hotelRoomId }) => hotelRoomId));
  const missing = (hotelRooms ?? []).filter(
    (room) => !room.isExternalSupplierOnly && !known.has(room.id),
  );
  if (!missing.length) return model;
  return {
    ...model,
    rooms: [
      ...model.rooms,
      ...missing.map((room) => ({
        id: `new-room-${room.id}`,
        hotelRoomId: room.id,
        name: room.displayName ?? room.name,
        mealPlanId: null,
        minimumStay: null,
        releaseDays: null,
        allotmentCount: null,
        sharedAllotmentCount: null,
        freeSale: null,
        translations: [],
      })),
    ],
  };
};

export const setRoom = (model, roomId, patch) => ({
  ...model,
  rooms: model.rooms.map((room) =>
    room.id === roomId ? { ...room, ...patch } : room,
  ),
});

/** Number of condition fields a room × season overrides. */
export const overridesCount = (seasonRoom) =>
  seasonRoom
    ? CONDITION_FIELDS.filter((field) => isSet(seasonRoom[field])).length
    : 0;

/** All condition overrides of a season, over every room. */
export const seasonOverridesCount = (model, seasonId) =>
  model.seasonRooms
    .filter((seasonRoom) => seasonRoom.seasonId === seasonId)
    .reduce((total, seasonRoom) => total + overridesCount(seasonRoom), 0);

/** Clears every condition override of a room × season (rates and offered
 * stay). */
export const resetSeasonRoomConditions = (model, at) =>
  findSeasonRoom(model, at.seasonId, at.roomId)
    ? setSeasonRoom(
        model,
        at,
        Object.fromEntries(
          CONDITION_FIELDS.map((field) => [
            field,
            field === 'translations' ? [] : null,
          ]),
        ),
      )
    : model;
